import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rolloverAPI } from '../../api/endpoints';
import { formatTimeAgo, formatXPUNT, getUserId } from '../../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaClock, FaCheck, 
  FaPlus, FaLock, FaUnlock, FaShare, FaCopy,
  FaChartLine, FaTrophy, FaFire, FaUser,
  FaTimes, FaSave, FaInfoCircle, FaUpload,
  FaGlobe, FaSpinner, FaSync
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './RolloverDetail.css';

const PLATFORMS = ['SportyBet', 'Bet9ja', '1xBet', 'Betway', 'Bet365', 'Other'];
const MARKETS = ['Multiple', 'Single', 'Accumulator', 'System'];

// ==================== HELPERS (outside component) ====================

/**
 * Normalize a bet's result regardless of the backend field naming.
 * Backend may return any of: is_marked+marked_result, result, status, outcome
 * Returns 'won' | 'loss' | 'pending'
 */
const getBetResult = (bet) => {
  if (!bet) return 'pending';

  // Primary contract used by xpunters_mark_bet_code_v2
  if (bet.is_marked === true && bet.marked_result === 'won') return 'won';
  if (bet.is_marked === true && (bet.marked_result === 'loss' || bet.marked_result === 'lost')) return 'loss';

  // Fallback: alternate field names
  const raw = (bet.result || bet.status || bet.marked_result || bet.outcome || '')
    .toString()
    .toLowerCase()
    .trim();

  if (['won', 'win', 'success', 'successful', 'w'].includes(raw)) return 'won';
  if (['loss', 'lost', 'fail', 'failed', 'l'].includes(raw)) return 'loss';

  return 'pending';
};

/**
 * Get the actual last bet by created_at (not array position).
 * API may return bets newest-first, so index-based access is unsafe.
 */
const getLastBet = (betsArr) => {
  if (!betsArr || betsArr.length === 0) return null;
  return [...betsArr].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
  )[betsArr.length - 1];
};

/**
 * Safely extract an array from a variety of API response shapes.
 */
const extractBetsArray = (res) => {
  if (!res) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.bets)) return d.bets;
  if (Array.isArray(d?.data?.bets)) return d.data.bets;
  return [];
};

/**
 * Safely extract a single game object from a variety of API response shapes.
 */
const extractGameObject = (res) => {
  if (!res) return null;
  const d = res.data;
  if (d?.data && typeof d.data === 'object' && !Array.isArray(d.data)) return d.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) return d;
  return null;
};

// ==================== COMPONENT ====================
const RolloverDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // ==================== STATES ====================
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [bets, setBets] = useState([]);
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Add bet form
  const [showAddBetForm, setShowAddBetForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [betForm, setBetForm] = useState({
    title: '',
    description: '',
    code: '',
    platform: '',
    number_of_games: 1,
    market_type: 'Multiple',
    odds: '',
    win_rate: '',
    image_data: null,
  });

  const currentUserId = getUserId();

  // ==================== FETCH GAME DATA ====================
  const fetchGameData = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const [gameRes, betsRes, joinedRes] = await Promise.all([
        rolloverAPI.getSingle(id),
        rolloverAPI.getBets(id).catch((err) => {
          // Do NOT fail the whole page if bets endpoint fails
          console.warn('[RolloverDetail] getBets failed:', err?.response?.status, err?.response?.data);
          return { data: [] };
        }),
        isAuthenticated 
          ? rolloverAPI.checkJoined(id).catch((err) => {
              console.warn('[RolloverDetail] checkJoined failed:', err?.response?.status);
              return { data: { joined: false } };
            })
          : Promise.resolve({ data: { joined: false } }),
      ]);

      const gameData = extractGameObject(gameRes);
      let betsData = extractBetsArray(betsRes);

      // Sort oldest → newest so index math (Round #N) is correct
      betsData = [...betsData].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
      );

      setGame(gameData);
      setBets(betsData);
      setJoined(joinedRes?.data?.joined || false);

      // ---- Debug: inspect the last bet's real shape ----
      const last = getLastBet(betsData);
      if (last) {
        console.log('[RolloverDetail] Last bet:', {
          id: last.id,
          created_at: last.created_at,
          is_marked: last.is_marked,
          marked_result: last.marked_result,
          result: last.result,
          status: last.status,
          outcome: last.outcome,
          normalized: getBetResult(last),
        });
      } else {
        console.log('[RolloverDetail] No bets returned. Raw betsRes:', betsRes?.data);
      }
      console.log('[RolloverDetail] Game status:', gameData?.status);
    } catch (error) {
      console.error('[RolloverDetail] Failed to fetch rollover:', error);
      if (showLoader) {
        toast.error('Failed to load rollover game');
        navigate('/rollover');
      }
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  // Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchGameData(false);
    setRefreshing(false);
  };

  // Initial load
  useEffect(() => {
    fetchGameData(true);
  }, [fetchGameData]);

  // Auto-refresh on tab focus / visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchGameData(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [fetchGameData]);

  // Polling every 15s while tab is visible (picks up admin approvals)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchGameData(false);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchGameData]);

  // ==================== HANDLE JOIN ====================
  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const response = await rolloverAPI.join(id);
      if (response.data?.success) {
        setJoined(true);
        toast.success('Joined rollover game!');
        await fetchGameData(false);
      } else {
        toast.error(response.data?.message || 'Failed to join');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== HANDLE SHARE ====================
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: game?.title || 'XPunters Rollover',
        text: `Check out this rollover game: ${game?.title}`,
        url: url,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  // ==================== ADD BET FORM HANDLERS ====================
  const handleBetFormChange = (e) => {
    const { name, value } = e.target;
    setBetForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const resetBetForm = () => {
    setBetForm({
      title: '',
      description: '',
      code: '',
      platform: '',
      number_of_games: 1,
      market_type: 'Multiple',
      odds: '',
      win_rate: '',
      image_data: null,
    });
    setImageFile(null);
    setImagePreview(null);
    setShowAddBetForm(false);
  };

  // ==================== CAN ADD NEXT BET? ====================
  const canAddNewBet = useCallback(() => {
    const lastBet = getLastBet(bets);
    if (!lastBet) return true; // first bet is always allowed
    return getBetResult(lastBet) === 'won';
  }, [bets]);

  const getLastBetStatus = useCallback(() => {
    const lastBet = getLastBet(bets);
    if (!lastBet) return null;

    const lastBetNumber = bets.length;
    const result = getBetResult(lastBet);

    if (result === 'won') {
      return { 
        status: 'won', 
        message: `Round #${lastBetNumber} won! You can now add Round #${lastBetNumber + 1}.` 
      };
    }
    if (result === 'loss') {
      return { 
        status: 'lost', 
        message: `Round #${lastBetNumber} was LOST. This rollover has ended.` 
      };
    }
    return { 
      status: 'pending', 
      message: `Round #${lastBetNumber} is awaiting results. Mark it as WON to add Round #${lastBetNumber + 1}.` 
    };
  }, [bets]);

  // ==================== HANDLE ADD BET ====================
  const handleAddBet = async (e) => {
    e.preventDefault();

    if (!betForm.title || !betForm.code || !betForm.number_of_games || !betForm.odds) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!canAddNewBet()) {
      toast.error('You must mark the previous bet as WON before adding a new one');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        title: betForm.title,
        description: betForm.description,
        code: betForm.code,
        platform: betForm.platform,
        number_of_games: parseInt(betForm.number_of_games),
        market_type: betForm.market_type,
        odds: parseFloat(betForm.odds),
        win_rate: parseFloat(betForm.win_rate) || 0,
      };
      if (imageFile && imagePreview) {
        submitData.image_data = imagePreview;
      }

      const response = await rolloverAPI.addBet(id, submitData);

      if (response.data?.success) {
        toast.success('Bet added successfully!');
        resetBetForm();
        await fetchGameData(false);
      } else {
        toast.error(response.data?.message || 'Failed to add bet');
      }
    } catch (error) {
      console.error('Add bet error:', error);
      toast.error(error.response?.data?.message || 'Failed to add bet');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== HANDLE COMPLETE ROLLOVER ====================
  const handleCompleteRollover = async () => {
    if (!window.confirm('Mark this rollover as complete? This cannot be undone.')) return;

    setSubmitting(true);
    try {
      const response = await rolloverAPI.complete(id);
      if (response.data?.success) {
        toast.success('Rollover completed!');
        await fetchGameData(false);
      } else {
        toast.error(response.data?.message || 'Failed to complete rollover');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== LOADING / NOT-FOUND ====================
  if (loading) {
    return (
      <div className="rollover-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="rollover-not-found">
        <h2>Rollover game not found</h2>
        <Link to="/rollover" className="btn btn-primary">
          <FaArrowLeft /> Back to Rollover
        </Link>
      </div>
    );
  }

  // ==================== COMPUTED VALUES ====================
  const isCreator = user && (
    String(game.creator_id) === String(currentUserId) ||
    String(game.author_id) === String(currentUserId) ||
    String(game.post_author) === String(currentUserId) ||
    String(game.user_id) === String(currentUserId)
  );
  
  const canJoin = !joined && game.status === 'active' && isAuthenticated && !isCreator;
  const lastBetStatus = getLastBetStatus();
  const canCreateNewBet = canAddNewBet();

  // ==================== RENDER ====================
  return (
    <div className="rollover-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="rollover-detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className={`rollover-status ${game.status}`}>
                  {game.status === 'active' ? '🟢 Active' : 
                   game.status === 'completed' ? '✅ Completed' : 
                   '❌ Failed'}
                </span>
                {game.is_private && (
                  <span className="detail-private"><FaLock /> Private</span>
                )}
              </div>
              <div className="detail-actions">
                <button 
                  onClick={handleRefresh} 
                  className="action-btn" 
                  title="Refresh"
                  disabled={refreshing}
                >
                  {refreshing ? <FaSpinner className="spin" /> : <FaSync />}
                </button>
                <button onClick={handleShare} className="action-btn" title="Share">
                  <FaShare />
                </button>
              </div>
            </div>

            <h1 className="detail-title">{game.title}</h1>
            {game.description && (
              <p className="detail-description">{game.description}</p>
            )}

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{game.subscribers_count || 0}</span>
                  <span className="stat-label">Followers</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaChartLine className="stat-icon" />
                <div>
                  <span className="stat-value">{bets.length}</span>
                  <span className="stat-label">Total Bets</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaFire className="stat-icon" />
                <div>
                  <span className="stat-value">{game.current_streak || 0}</span>
                  <span className="stat-label">Current Streak</span>
                </div>
              </div>
            </div>

            {/* Join Section for non-creators */}
            {canJoin && (
              <div className="join-section">
                <button 
                  onClick={handleJoin} 
                  className="btn btn-primary btn-lg"
                  disabled={submitting}
                >
                  {submitting ? <LoadingSpinner size="small" /> : 'Join Rollover'}
                </button>
                <p className="join-hint">
                  Follow this rollover and get notified when new bets are added
                </p>
              </div>
            )}

            {joined && !isCreator && (
              <div className="joined-badge">
                <FaCheck /> You're following this rollover
              </div>
            )}

            {/* ==================== CREATOR: ADD BET SECTION ==================== */}
            {isCreator && game.status === 'active' && (
              <div className="creator-bet-section">
                {bets.length > 0 && lastBetStatus && (
                  <div className={`last-bet-status ${lastBetStatus.status}`}>
                    <FaInfoCircle className="status-icon" />
                    <div className="status-content">
                      <strong>
                        {lastBetStatus.status === 'won' && '✅ Great! Last bet won'}
                        {lastBetStatus.status === 'pending' && '⏳ Awaiting last bet result'}
                        {lastBetStatus.status === 'lost' && '❌ Last bet lost'}
                      </strong>
                      <span>{lastBetStatus.message}</span>
                    </div>
                  </div>
                )}

                {!showAddBetForm && canCreateNewBet && (
                  <button 
                    className="btn btn-primary btn-lg add-bet-btn"
                    onClick={() => setShowAddBetForm(true)}
                  >
                    <FaPlus /> Add {bets.length === 0 ? 'First Bet' : `Round #${bets.length + 1}`}
                  </button>
                )}

                {showAddBetForm && (
                  <div className="add-bet-form-container">
                    <div className="form-header">
                      <h3>
                        <FaPlus /> Add Bet #{bets.length + 1}
                      </h3>
                      <button 
                        className="close-form-btn"
                        onClick={resetBetForm}
                        type="button"
                      >
                        <FaTimes />
                      </button>
                    </div>

                    <form onSubmit={handleAddBet} className="add-bet-form">
                      <div className="form-group">
                        <label>Bet Title *</label>
                        <input
                          type="text"
                          name="title"
                          placeholder="e.g., Weekend Accumulator"
                          value={betForm.title}
                          onChange={handleBetFormChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="description"
                          placeholder="Describe this bet..."
                          value={betForm.description}
                          onChange={handleBetFormChange}
                          rows={2}
                        />
                      </div>

                      <div className="form-group">
                        <label>Bet Code *</label>
                        <input
                          type="text"
                          name="code"
                          placeholder="e.g., ABC123XYZ"
                          value={betForm.code}
                          onChange={handleBetFormChange}
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Platform</label>
                          <select
                            name="platform"
                            value={betForm.platform}
                            onChange={handleBetFormChange}
                          >
                            <option value="">Select Platform</option>
                            {PLATFORMS.map(p => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Market Type</label>
                          <select
                            name="market_type"
                            value={betForm.market_type}
                            onChange={handleBetFormChange}
                          >
                            {MARKETS.map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Number of Games *</label>
                          <input
                            type="number"
                            name="number_of_games"
                            placeholder="e.g., 5"
                            value={betForm.number_of_games}
                            onChange={handleBetFormChange}
                            min="1"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Odds *</label>
                          <input
                            type="number"
                            name="odds"
                            placeholder="e.g., 5.50"
                            value={betForm.odds}
                            onChange={handleBetFormChange}
                            step="0.01"
                            min="1.01"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Win Rate (%)</label>
                        <input
                          type="number"
                          name="win_rate"
                          placeholder="e.g., 75.5"
                          value={betForm.win_rate}
                          onChange={handleBetFormChange}
                          step="0.1"
                          min="0"
                          max="100"
                        />
                      </div>

                      <div className="form-group">
                        <label>Bet Image (Optional)</label>
                        <div className="image-upload-area">
                          {imagePreview ? (
                            <div className="image-preview">
                              <img src={imagePreview} alt="Preview" />
                              <button 
                                type="button" 
                                className="remove-image" 
                                onClick={removeImage}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ) : (
                            <label className="upload-label">
                              <FaUpload />
                              <span>Click to upload image</span>
                              <span className="upload-hint">PNG, JPG, GIF up to 5MB</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                hidden
                              />
                            </label>
                          )}
                        </div>
                      </div>

                      <div className="form-actions">
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={resetBetForm}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={submitting}
                        >
                          {submitting ? <LoadingSpinner size="small" /> : (
                            <><FaSave /> Add Bet</>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* ==================== BET TIMELINE ==================== */}
            <div className="bets-section">
              <div className="bets-header">
                <h3>📊 Bet Timeline ({bets.length})</h3>
                {game.current_streak > 0 && (
                  <span className="streak-indicator">
                    🔥 Streak: {game.current_streak}
                  </span>
                )}
              </div>

              {bets.length === 0 ? (
                <div className="empty-state-small">
                  <FaFire className="empty-icon" />
                  <p>No bets added yet</p>
                  {isCreator && (
                    <small>Click "Add First Bet" above to start your rollover</small>
                  )}
                </div>
              ) : (
                <div className="bets-timeline">
                  {bets.map((bet, index) => {
                    const result = getBetResult(bet);
                    return (
                      <div 
                        key={bet.id} 
                        className={`bet-timeline-item ${index === bets.length - 1 ? 'last-bet' : ''}`}
                      >
                        <div className={`timeline-number ${result === 'won' ? 'won' : result === 'loss' ? 'lost' : 'pending'}`}>
                          {result === 'won' ? (
                            <FaCheck />
                          ) : result === 'loss' ? (
                            <FaTimes />
                          ) : (
                            index + 1
                          )}
                        </div>

                        <div className="timeline-content">
                          <Link to={`/bet-codes/${bet.id}`} className="bet-link">
                            <div className="bet-header">
                              <span className="bet-title">{bet.title}</span>
                              <span className="bet-odds">Odds: {bet.odds}</span>
                            </div>
                            
                            {bet.code && (
                              <div className="bet-code-display">{bet.code}</div>
                            )}
                            
                            <div className="bet-footer">
                              <span className="bet-games">
                                {bet.number_of_games} game{bet.number_of_games !== 1 ? 's' : ''}
                              </span>
                              
                              {bet.platform && (
                                <span className="bet-platform-tag">{bet.platform}</span>
                              )}
                              
                              {result === 'won' ? (
                                <span className="bet-result won">
                                  <FaCheck /> Won
                                </span>
                              ) : result === 'loss' ? (
                                <span className="bet-result loss">
                                  <FaTimes /> Lost
                                </span>
                              ) : (
                                <span className="bet-result pending">
                                  <FaClock /> Pending
                                </span>
                              )}
                            </div>

                            {bet.created_at && (
                              <div className="bet-timestamp">
                                Added {formatTimeAgo(bet.created_at)}
                              </div>
                            )}
                          </Link>
                        </div>
                      </div>
                    );
                  })}

                  {/* Next round placeholder */}
                  {isCreator && game.status === 'active' && canCreateNewBet && !showAddBetForm && (
                    <div className="bet-timeline-item next-bet-placeholder">
                      <div className="timeline-number next">
                        {bets.length + 1}
                      </div>
                      <div className="timeline-content">
                        <button 
                          className="add-next-bet-btn"
                          onClick={() => setShowAddBetForm(true)}
                          type="button"
                        >
                          <FaPlus /> Add Round #{bets.length + 1}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="info-card">
            <h4>ℹ️ Rollover Info</h4>
            <div className="info-item">
              <span>Creator</span>
              <span>{game.creator_name || game.author || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span>Status</span>
              <span className={game.status}>{game.status}</span>
            </div>
            <div className="info-item">
              <span>Total Bets</span>
              <span>{bets.length}</span>
            </div>
            <div className="info-item">
              <span>Current Streak</span>
              <span className="gold">{game.current_streak || 0}</span>
            </div>
            <div className="info-item">
              <span>Created</span>
              <span>{formatTimeAgo(game.created_at)}</span>
            </div>
          </div>

          {/* Creator Actions */}
          {isCreator && game.status === 'active' && (
            <div className="info-card creator-actions">
              <h4>🎯 Creator Actions</h4>
              
              {canCreateNewBet && !showAddBetForm && (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowAddBetForm(true)}
                  type="button"
                >
                  <FaPlus /> Add Next Bet
                </button>
              )}
              
              {!canCreateNewBet && bets.length > 0 && (
                <div className="creator-hint">
                  <FaInfoCircle />
                  <span>
                    {lastBetStatus?.status === 'pending' && 'Mark last bet as WON to continue'}
                    {lastBetStatus?.status === 'lost' && 'Rollover ended - last bet lost'}
                  </span>
                </div>
              )}
              
              {bets.length > 0 && (
                <button 
                  className="btn btn-success btn-sm"
                  onClick={handleCompleteRollover}
                  disabled={submitting}
                  type="button"
                >
                  {submitting ? <LoadingSpinner size="small" /> : (
                    <><FaCheck /> Complete Rollover</>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Subscriber info */}
          {!isCreator && joined && (
            <div className="info-card">
              <h4>🔔 Notifications</h4>
              <p className="notification-hint">
                You'll be notified when the creator adds a new bet to this rollover.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolloverDetail;